import { useState, useEffect } from "react";
import { Button, Modal, Form, Upload, UploadFile, UploadProps, Input, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import EditorComponent from "../../../components/EditorComponent";

const PreviewAndSendEmailModal = (props: any) => {
  const { isModalOpen, assignment, user, emailType, onCancel, onSendEmail, title } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onFinish = (values: any) => {
    values["id"] = assignment?.id;
    onSendEmail(values, setLoading, form, fileList);
    setFileList([]);
  };

  const onFinishFailed = (errorInfo: any) => {
    setFileList([]);
    console.log("Failed:", errorInfo);
  };

  useEffect(() => {
    form.setFieldValue(emailType, assignment ? assignment[`${emailType}_preview`] : "");
    if (assignment) {
      form.setFieldValue("recipient_emails", assignment.email);
      form.setFieldValue(
        "subject",
        `Feasibility Study for ${assignment?.brand?.name} EV : ${assignment.customer_name}`
      );
    }
    if (user) {
      form.setFieldValue("from", user.email);
      form.setFieldValue("cc_emails", ccEmails());
    }
  }, [assignment, user]);

  const ccEmails = () => {
    if (assignment) {
      let emails = user.email;
      if (assignment.dealer_email) {
        emails = emails + " " + assignment.dealer_email;
      }
      if (assignment.city_manager_email) {
        emails = emails + " " + assignment.city_manager_email;
      }
      if (assignment.zonal_manager_email) {
        emails = emails + " " + assignment.zonal_manager_email;
      }
      return emails;
    } else {
      return user.email;
    }
  };

  const uProps: UploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file, fileList) => {
      const maxFileSize = 250 * 1024 * 1024; // 250MB limit
      const maxLimit = 10;
      if (fileList.length > maxLimit) {
        message.error("Only 10 files allowed");
        return false;
      }

      if (file.size > maxFileSize) {
        message.error(`${file.name} file upload failed (exceeds 250MB)`);
        console.error("File size exceeds the limit of 250MB");
        return false;
      }

      setFileList((prevList) => [...prevList, file]);

      return false;
    },
    multiple: true,
    accept: "application/pdf",
    fileList
  };

  return (
    <Modal
      title={`${title} - ${assignment?.ecb_install_code || assignment?.ecb_survey_code}`}
      open={isModalOpen}
      onCancel={() => {
        setFileList([]);
        form.resetFields();
        onCancel();
      }}
      width={1000}
      footer={false}
      destroyOnClose={false}
    >
      <Form
        name="preview_email_form"
        form={form}
        initialValues={{}}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Form.Item
          name="from"
          label="From"
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="recipient_emails"
          label="To"
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="subject"
          label="Subject"
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={emailType}
          label="Body"
          rules={[{ required: true, message: "Please input email" }]}
        >
          <EditorComponent />
        </Form.Item>

        <Form.Item name="cc_emails" label="CC">
          <Input />
        </Form.Item>

        <Form.Item name="bcc_emails" label="BCC">
          <Input />
        </Form.Item>

        <Upload {...uProps}>
          <Button icon={<UploadOutlined />}>Select File</Button>
        </Upload>

        <Form.Item wrapperCol={{ offset: 0, span: 24 }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              type="default"
              onClick={() => {
                form.resetFields();
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {loading ? "Sending ...." : "Send Email"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PreviewAndSendEmailModal;
